// src/features/home/components/ExpiryPanel.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, CircularProgress, Divider, IconButton,
  Tooltip, Typography,
} from '@mui/material';
import { CalendarMonth as CalendarIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { complianceAPI } from '../../compliance/services/compliance';
import HomeCard from './HomeCard';

function formatDate(daysOffset) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + daysOffset);
  return d.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' });
}

const BUCKETS = [
  { key: 'today',    labelKey: 'home:expiry_panel.buckets.today',    sublabel: () => formatDate(0), test: (d) => d === 0 },
  { key: 'tomorrow', labelKey: 'home:expiry_panel.buckets.tomorrow', sublabel: () => formatDate(1), test: (d) => d === 1 },
  { key: 'week',     labelKey: 'home:expiry_panel.buckets.week',     sublabel: null,              test: (d) => d >= 2 && d <= 7 },
  { key: 'month',    labelKey: 'home:expiry_panel.buckets.month',    sublabel: null,              test: (d) => d >= 8 && d <= 30 },
];

const TYPE_COLOR = {
  msds:       'info.main',
  lot:        'warning.main',
  delegation: 'secondary.main',
};

const TYPE_LABEL_KEY = {
  msds:       'home:expiry_panel.types.msds',
  lot:        'home:expiry_panel.types.lot',
  delegation: 'home:expiry_panel.types.delegation',
};

function Dot({ type }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: 7,
        height: 7,
        borderRadius: '50%',
        bgcolor: TYPE_COLOR[type],
        flexShrink: 0,
        mr: 1,
      }}
    />
  );
}

function AgendaItem({ item, navigate, t }) {
  return (
    <Box
      onClick={() => navigate(item.navigateTo)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1,
        px: 1,
        borderRadius: 1,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Dot type={item.type} />
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
        {item.name}
      </Typography>
      <Typography variant="caption" color="text.tertiary" sx={{ ml: 1.5, flexShrink: 0 }}>
        {t(TYPE_LABEL_KEY[item.type])}
      </Typography>
    </Box>
  );
}

function BucketSection({ bucket, items, navigate, showDivider, t }) {
  const sublabel = typeof bucket.sublabel === 'function' ? bucket.sublabel() : bucket.sublabel;

  return (
    <>
      {showDivider && <Divider sx={{ my: 2 }} />}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
          <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
            {t(bucket.labelKey)}
          </Typography>
          {sublabel && (
            <Typography variant="caption" color="text.tertiary">
              {sublabel}
            </Typography>
          )}
        </Box>

        {items.length === 0 ? (
          <Typography variant="body2" color="text.tertiary" fontStyle="italic" sx={{ pl: 1, pb: 0.5 }}>
            {t('home:expiry_panel.none')}
          </Typography>
        ) : (
          items.map((item) => (
            <AgendaItem key={item.id} item={item} navigate={navigate} t={t} />
          ))
        )}
      </Box>
    </>
  );
}

export default function ExpiryPanel() {
  const { t } = useTranslation(['home']);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await complianceAPI.getExpiryOverview();
      const raw = res?.data?.data?.items || [];
      setItems(raw.map((item) => ({
        id: item.id,
        type: item.type,
        name: item.name,
        daysUntil: item.days_until,
        navigateTo: item.navigate_to,
      })));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const bucketed = BUCKETS.map((bucket) => ({
    bucket,
    items: items.filter((item) => bucket.test(item.daysUntil)),
  }));

  return (
    <HomeCard
      icon={<CalendarIcon sx={{ fontSize: 20 }} />}
      title={t('home:expiry_panel.title')}
      action={(
        <Tooltip title={t('home:expiry_panel.refresh')}>
          <IconButton size="small" onClick={fetchAll} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    >
      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        bucketed.map(({ bucket, items: bucketItems }, i) => (
          <BucketSection
            key={bucket.key}
            bucket={bucket}
            items={bucketItems}
            navigate={navigate}
            showDivider={i > 0}
            t={t}
          />
        ))
      )}
    </HomeCard>
  );
}
